import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, TrendingUp, Sparkles, Gauge, Users } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

const HERO_MOCKUP =
  "https://images.unsplash.com/photo-1772272935464-2e90d8218987?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxzYWFzJTIwZGFzaGJvYXJkJTIwZGFya3xlbnwwfHx8fDE3ODI1ODcyOTZ8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Background effects */}
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[60rem] h-[60rem] radial-glow opacity-60 pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[40rem] h-[40rem] radial-glow opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Built for AI-First Growth
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="mt-6 font-display font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
            data-testid="hero-headline"
          >
            The <span className="text-gradient-brand">Digital Growth</span>
            <br /> Authority.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-6 text-lg md:text-xl text-white/65 max-w-xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            High-end websites, SEO &amp; AI visibility systems that help ambitious
            businesses dominate search, get cited by ChatGPT, and convert
            visitors into revenue.
          </motion.p>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-3 text-sm text-white/45 max-w-xl"
          >
            We build premium websites, SEO + GEO engines, branding, and
            conversion-focused digital experiences that quietly out-perform
            agencies charging 4&times; more.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#audit"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-7 py-4 rounded-full hover:shadow-[0_0_40px_rgba(0,229,255,0.45)] transition-all duration-300"
            >
              Book Free Audit
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-7 py-4 rounded-full hover:bg-white/10 hover:border-white/20 transition-all duration-300 backdrop-blur-md"
            >
              <PlayCircle className="w-4 h-4" />
              Our Services
            </a>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={5}
            className="mt-12 flex items-center gap-5"
          >
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full border-2 border-black bg-gradient-to-br from-blue-500 to-cyan-400"
                  style={{ opacity: 1 - (i - 1) * 0.15 }}
                />
              ))}
            </div>
            <div className="text-sm">
              <div className="text-white font-semibold">150+ founders</div>
              <div className="text-white/50">trust EchoWings every quarter</div>
            </div>
          </motion.div>
        </div>

        {/* Right: dashboard mockup with floating cards */}
        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
            className="relative"
            data-testid="hero-mockup"
          >
            <div className="absolute -inset-6 bg-gradient-to-tr from-blue-600/30 to-cyan-400/20 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,85,255,0.25)]">
              <img
                src={HERO_MOCKUP}
                alt="Premium analytics dashboard"
                className="w-full h-auto block"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Floating stat cards */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="absolute -left-6 top-12 glass-card rounded-2xl px-4 py-3 flex items-center gap-3 animate-float"
              data-testid="hero-float-card-rankings"
            >
              <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">
                  Google Rankings
                </div>
                <div className="text-base font-bold">
                  #1 <span className="text-cyan-400">↑ 28</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05 }}
              className="absolute -right-4 top-1/3 glass-card rounded-2xl px-4 py-3 flex items-center gap-3 animate-float-slow"
              data-testid="hero-float-card-ai"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">
                  AI Visibility
                </div>
                <div className="text-base font-bold">98%</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="absolute -left-4 bottom-16 glass-card rounded-2xl px-4 py-3 flex items-center gap-3 animate-float"
              data-testid="hero-float-card-speed"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">
                  Website Speed
                </div>
                <div className="text-base font-bold">99 / 100</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35 }}
              className="absolute -right-6 -bottom-4 glass-card rounded-2xl px-4 py-3 flex items-center gap-3 animate-float-slow"
              data-testid="hero-float-card-leads"
            >
              <div className="w-10 h-10 rounded-xl bg-fuchsia-500/15 flex items-center justify-center">
                <Users className="w-5 h-5 text-fuchsia-400" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">
                  Leads / Month
                </div>
                <div className="text-base font-bold">+184</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
